import { createSlice } from "@reduxjs/toolkit"
import { createMappingAsync, getScenarioDetails } from "../HarmonizationSlice"
import { ISampleData } from "./MapInsightsTable"
import { CreateDropDownOptions } from "../../../util/CommonMethods"
import { IDropDownOptions } from "../../../util/interface"

interface IState {
  mappedData: ISampleData[]
  absoluteMapped: ISampleData[]
  approximateMapped: ISampleData[]
  notMapped: ISampleData[]
  selectedForApproval: number[]
  activeTab: string
  showMappingSummary: boolean
  // temporary var
  saveClicked: boolean
  filter: {
    countries: IDropDownOptions[]
    selectedCountries: string[]
    categories: IDropDownOptions[]
    selectedCategories: string[]
    brand: IDropDownOptions[]
    selectedBrands: string[]
    dynamicOptions: Record<string, IDropDownOptions[]>
    dynamicSelected: Record<string, string[]>
    dynamicLabels: Record<string, string>
    dynamicKeyToRowKey?: Record<string, string>
  }
  mappingAccuracy: number
}

const initialState: IState = {
  selectedForApproval: [],
  mappedData: [],
  absoluteMapped: [],
  approximateMapped: [],
  notMapped: [],
  activeTab: "All",
  showMappingSummary: false,
  saveClicked: false,
  filter: {
    countries: [],
    selectedCountries: [],
    categories: [],
    selectedCategories: [],
    brand: [],
    selectedBrands: [],
    dynamicOptions: {},
    dynamicSelected: {},
    dynamicLabels: {},
    dynamicKeyToRowKey: {},
  },
  mappingAccuracy: 65,
}

const mapSlice = createSlice({
  name: "harmonization",
  initialState,
  reducers: {
    setMappedData: (state, action) => {
      console.log("SET MAPPED DATA ACTION", action.payload)
      const incomingData = Array.isArray(action.payload) ? action.payload : []
      const normalized = incomingData.map((d: any) => ({
        ...d,
        status: (d?.similarity ?? 0) >= 0.9 ? 1 : 2,
      }))
      state.mappedData = normalized as ISampleData[]

      const absoluteMapped = normalized.filter(
        (d: any) => (d?.similarity ?? 0) >= 0.9,
      )

      const notMapped = normalized.filter((d: any) => (d?.similarity ?? 0) < 0.8)

      const approximateMapped = normalized.filter(
        (d: any) => (d?.similarity ?? 0) >= 0.8 && (d?.similarity ?? 0) < 0.9,
      )

      state.absoluteMapped = absoluteMapped as ISampleData[]
      state.approximateMapped = approximateMapped as ISampleData[]
      state.notMapped = notMapped as ISampleData[];
    },
    setInsightsData: (state) => {
      const mapped = state.mappedData as any[]

      const usesSimilarity =
        mapped.length > 0 && typeof (mapped[0] as any).similarity === "number"

      const getPercent = (value: any) => {
        const str = String(value ?? "0").replace("%", "")
        const num = parseFloat(str)
        return Number.isFinite(num) ? num : 0
      }

      const absoluteMapped = usesSimilarity
        ? mapped.filter((d: any) => (d?.similarity ?? 0) >= 0.9)
        : mapped.filter((d: any) => getPercent((d as any).mapping_accuracy) >= 90)

      const notMapped = usesSimilarity
        ? mapped.filter((d: any) => (d?.similarity ?? 0) < 0.8)
        : mapped.filter((d: any) => getPercent((d as any).mapping_accuracy) < 80)

      const approximateMapped = usesSimilarity
        ? mapped.filter(
            (d: any) => (d?.similarity ?? 0) >= 0.8 && (d?.similarity ?? 0) < 0.9,
          )
        : mapped.filter((d: any) => {
            const p = getPercent((d as any).mapping_accuracy)
            return p >= 80 && p < 90
          })

      state.absoluteMapped = absoluteMapped as ISampleData[]
      state.approximateMapped = approximateMapped as ISampleData[]
      state.notMapped = notMapped as ISampleData[]
    },
    setSelectedForApproval: (state, action) => {
      const id = action.payload
      console.log(action.payload)
      if (state.selectedForApproval.includes(id)) {
        state.selectedForApproval = state.selectedForApproval.filter(
          (item) => item !== id,
        )
      } else {
        state.selectedForApproval = [...state.selectedForApproval, id]
      }
    },
    selectAllForApproval: (state, action) => {
      state.selectedForApproval = action.payload
    },
    processApproval: (state, action) => {
      const actionType = action.payload
      state.selectedForApproval.forEach((id) => {
        state.mappedData = state.mappedData.map((data) => {
          if (data.id === id) {
            actionType === "approve"
              ? (data.status = 1)
              : actionType === "reject"
              ? (data.status = 0)
              : (data.status = 2)
          }
          return data
        })
      })

      const mappedData = state.mappedData

      const absoluteMapped = mappedData.filter(
      (data) => data.similarity >= 0.9
    );

    const notMapped = mappedData.filter(
      (data) => data.similarity < 0.8
    );

    const approximateMapped = mappedData.filter(
      (data) => data.similarity >= 0.8 && data.similarity < 0.9
    );

      state.absoluteMapped = absoluteMapped
      state.approximateMapped = approximateMapped
      state.notMapped = notMapped
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload
    },
    setDynamicFilters: (
      state,
      action: {
        payload: {
          optionsByKey: Record<string, any[]>
          labelsByKey?: Record<string, string>
          keyMapByComposite?: Record<string, string>
        }
      },
    ) => {
      const { optionsByKey, labelsByKey = {}, keyMapByComposite = {} } = action.payload
      const dynOptions: Record<string, IDropDownOptions[]> = {}
      Object.entries(optionsByKey).forEach(([key, values]) => {
        dynOptions[key] = CreateDropDownOptions(values as any[])
        if (!state.filter.dynamicSelected[key]) {
          state.filter.dynamicSelected[key] = []
        }
      })
      state.filter.dynamicOptions = dynOptions
      state.filter.dynamicLabels = labelsByKey
      state.filter.dynamicKeyToRowKey = keyMapByComposite
    },
    updateDynamicFilter: (state, action) => {
      const { key, value } = action.payload as { key: string; value: string[] }
      state.filter.dynamicSelected[key] = value
    },
    setShowMappingSummary: (state, action) => {
      state.showMappingSummary = action.payload
    },
    resetSelectedForApproval: (state) => {
      state.selectedForApproval = []
    },
    setSaveClicked: (state, action) => {
      state.saveClicked = action.payload
    },
    configureFilter: (state, action) => {
      const { countries, categories, brands } = action.payload

      state.filter.countries = CreateDropDownOptions(countries)
      state.filter.categories = CreateDropDownOptions(categories)
      state.filter.brand = CreateDropDownOptions(brands)
    },
    updateOption: (state, action) => {
      const { filter, value } = action.payload

      if (filter === "countries") {
        state.filter.selectedCountries = value
      } else if (filter === "categories") {
        state.filter.selectedCategories = value
      } else if (filter === "brand") {
        state.filter.selectedBrands = value
      }
    },
    clearFilters: (state) => {
      state.filter.selectedBrands = []
      state.filter.selectedCategories = []
      state.filter.selectedCountries = []
      state.filter.dynamicSelected = {}
    },
    setMappingAccuracy: (state, action) => {
      state.mappingAccuracy = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createMappingAsync.fulfilled, (state, action: any) => {
      try {
        const raw = action.payload
        const incomingData = Array.isArray(raw)
          ? raw
          : typeof raw === "string"
          ? JSON.parse(raw)
          : Array.isArray(raw?.data)
          ? raw.data
          : typeof raw?.data === "string"
          ? JSON.parse(raw.data)
          : []

        const normalized = (incomingData as any[]).map((d: any) => ({
          ...d,
          id : d.id ?? Math.random().toString(36).substring(2, 9),
          status: (d?.similarity ?? 0) >= 0.9 ? 1 : 2,
        }))

        state.mappedData = normalized as ISampleData[]

        const absoluteMapped = normalized.filter(
          (d: any) => (d?.similarity ?? 0) >= 0.9,
        )

        const notMapped = normalized.filter((d: any) => (d?.similarity ?? 0) < 0.8)

        const approximateMapped = normalized.filter(
          (d: any) => (d?.similarity ?? 0) >= 0.8 && (d?.similarity ?? 0) < 0.9,
        )

        state.absoluteMapped = absoluteMapped as ISampleData[]
        state.approximateMapped = approximateMapped as ISampleData[]
        state.notMapped = notMapped as ISampleData[]
      } catch (e) {
        // keep existing state on parse error
      }
    }),
    builder.addCase(getScenarioDetails.fulfilled, (state, action: any) => {
      console.log("SCENARIO DETAILS PAYLOAD", action.payload)
    })
  },
})

export const {
  setMappedData,
  setSelectedForApproval,
  processApproval,
  setActiveTab,
  setShowMappingSummary,
  resetSelectedForApproval,
  selectAllForApproval,
  setSaveClicked,
  configureFilter,
  updateOption,
  setInsightsData,
  clearFilters,
  setDynamicFilters,
  updateDynamicFilter,
} = mapSlice.actions

export default mapSlice.reducer
